import type { CSSProperties } from "react";

export interface MediaIntrinsicSize {
  width: number | null;
  height: number | null;
}

export interface MediaSizeBounds {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  /** Cap on how far a tiny thumbnail may be scaled up to meet the min floor. */
  maxUpscale: number;
}

// 370px width matches the library bubble cap; 440px height keeps portraits from
// dominating; the min floor lifts tiny thumbnails (capped by maxUpscale).
export const ATTENTION_MEDIA_BOUNDS: MediaSizeBounds = {
  minWidth: 120,
  minHeight: 120,
  maxWidth: 370,
  maxHeight: 440,
  maxUpscale: 2,
};

export interface MediaDisplaySize {
  width: number;
  height: number;
}

export type AttentionMediaKind = "image" | "video" | "gif";

// Fallback box width when intrinsic dims are unknown (pending uploads, posterless
// video, most GIFs); below maxWidth so it reads as a card, not a full-width panel.
const DEFAULT_MEDIA_WIDTH = 280;

const DEFAULT_MEDIA_ASPECT: Record<AttentionMediaKind, number> = {
  image: 1,
  gif: 1,
  video: 16 / 9,
};

// Fixed fallback box so no kind collapses to a blob when intrinsic dims are unknown.
export function defaultMediaBox(
  kind: AttentionMediaKind,
  bounds: MediaSizeBounds = ATTENTION_MEDIA_BOUNDS,
): MediaDisplaySize {
  const width = Math.min(DEFAULT_MEDIA_WIDTH, bounds.maxWidth);
  const height = Math.min(
    bounds.maxHeight,
    Math.round(width / DEFAULT_MEDIA_ASPECT[kind]),
  );
  return { width, height };
}

// Fits intrinsic w/h into the bounds preserving aspect ratio; null when unknown.
// Max bounds win over the min floor, so panoramas/skyscrapers stay below the floor
// on their short axis rather than crop.
export function fitMediaBox(
  intrinsic: MediaIntrinsicSize,
  bounds: MediaSizeBounds = ATTENTION_MEDIA_BOUNDS,
): MediaDisplaySize | null {
  const { width, height } = intrinsic;
  if (width === null || height === null || width <= 0 || height <= 0) {
    return null;
  }

  let displayWidth = width;
  let displayHeight = height;

  const growth = Math.min(
    bounds.maxUpscale,
    Math.max(1, bounds.minWidth / displayWidth, bounds.minHeight / displayHeight),
  );
  displayWidth *= growth;
  displayHeight *= growth;

  const shrink = Math.min(
    1,
    bounds.maxWidth / displayWidth,
    bounds.maxHeight / displayHeight,
  );
  displayWidth *= shrink;
  displayHeight *= shrink;

  return {
    width: Math.round(displayWidth),
    height: Math.round(displayHeight),
  };
}

// Fixed width/height reserved up front (no load-time shift): fitted box when
// intrinsic dims are known, else the kind's default box.
export function resolveMediaBoxStyle(
  intrinsic: MediaIntrinsicSize,
  kind: AttentionMediaKind,
  bounds: MediaSizeBounds = ATTENTION_MEDIA_BOUNDS,
): CSSProperties {
  const box = fitMediaBox(intrinsic, bounds) ?? defaultMediaBox(kind, bounds);
  return {
    width: box.width,
    height: box.height,
    aspectRatio: `${box.width} / ${box.height}`,
  };
}
